import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { softControlMotion } from '@/theme/interactions';

export interface TabItem {
  value: string;
  label: string;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Optional accessible label and ids for a set of associated tab panels. */
  ariaLabel?: string;
  idPrefix?: string;
}

const List = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};

  /* Horizontally scrollable on mobile: hidden scrollbar + a right-edge fade so an
     overflowing strip (e.g. Posts…Bookmarks at 390px) reads as "scroll for more"
     rather than a clipped/broken tab. */
  ${media.mobile`
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    -webkit-mask-image: linear-gradient(90deg, #000 0, #000 calc(100% - 26px), transparent 100%);
    mask-image: linear-gradient(90deg, #000 0, #000 calc(100% - 26px), transparent 100%);
  `}
`;

const Tab = styled.button<{ $active: boolean }>`
  background: ${({ theme, $active }) => ($active ? theme.color.hover : 'transparent')};
  border: none;
  flex-shrink: 0;
  border-bottom: 2px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  padding: 10px 14px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ theme, $active }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  ${softControlMotion}
  &:hover {
    background-color: ${({ theme }) => theme.color.hover};
  }
`;

export function Tabs({ items, value, onChange, ariaLabel, idPrefix }: TabsProps) {
  const index = items.findIndex((t) => t.value === value);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function moveTo(nextIndex: number) {
    onChange(items[nextIndex]!.value);
    tabRefs.current[nextIndex]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveTo((index + 1 + items.length) % items.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveTo((index - 1 + items.length) % items.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      moveTo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      moveTo(items.length - 1);
    }
  }

  return (
    <List role="tablist" aria-label={ariaLabel} aria-orientation="horizontal" onKeyDown={onKeyDown}>
      {items.map((t, i) => {
        const active = t.value === value;
        return (
          <Tab
            key={t.value}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${t.value}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${t.value}` : undefined}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            $active={active}
            onClick={() => onChange(t.value)}
          >
            {t.label}
          </Tab>
        );
      })}
    </List>
  );
}
